import {
  DEFAULT_CHINA_LOCALE,
  DEFAULT_LOCALE,
  DEFAULT_SC_LOCALE,
  LOCALES_WITH_REGION,
} from '../constants';

/**
 * Returns the locale with region if available, otherwise returns a default locale.
 *
 * @param locale - The locale string to be processed. It can be in the format of 'en' or 'en-US'.
 * @returns The locale string with region if available, otherwise a default locale.
 */
export const getLocaleWithRegion = (locale?: string): string => {
  if (!locale) {
    return DEFAULT_LOCALE;
  }
  if (locale.includes('-')) return locale;

  const localeWithRegion = LOCALES_WITH_REGION.find((l) => l.startsWith(locale));
  return localeWithRegion ?? locale;
};

/**
 * Attaches a language prefix to a given path.
 *
 * If the language is undefined, or matches the default Sitecore or China locale,
 * the path is returned without any language prefix.
 *
 * @param language - The language code to attach to the path. Can be undefined.
 * @param path - The path to which the language code should be attached. Defaults to an empty string.
 * @returns The path with the language prefix attached, if applicable.
 */
export const attachLanguageToPath = (language: string | undefined, path: string = ''): string => {
  if (
    !language ||
    language === DEFAULT_SC_LOCALE ||
    language.toLowerCase() === DEFAULT_CHINA_LOCALE.toLowerCase()
  ) {
    return `/${path.replace(/^\//, '')}`;
  }

  const localePrefix = `${language}/`;
  const cleanedPath = path.replace(/^\//, '');

  if (cleanedPath.toLowerCase().startsWith(localePrefix.toLowerCase())) {
    const localePattern = new RegExp(localePrefix, 'i');
    const updatedPath: string = cleanedPath.replace(localePattern, localePrefix);

    return `/${updatedPath}`;
  }

  return `/${localePrefix}${cleanedPath}`;
};

/**
 * Converts a locale string to BCP 47 format for use in e.g. lang and hreflang attributes.
 * Defaults to "en" if not provided, or language code if the input is non-standard
 * or includes script subtags.
 */
export const localeToBcp47 = (locale: string | undefined): string => {
  const SEPARATOR = '-';
  const MAXIMUM_STANDARD_LOCALE_LENGTH = 5; // E.g. 'en-US' is 5 characters long. Anything longer is likely non-standard (e.g. 'en-apac', 'fr-mea'; We don't have locales with 3 character language identifiers) or includes script subtags (e.g. 'zh-Hant-TW').

  if (!locale) return DEFAULT_SC_LOCALE;

  if (locale.length > MAXIMUM_STANDARD_LOCALE_LENGTH) {
    const [lang] = locale.split(SEPARATOR);
    return `${lang.toLowerCase()}`;
  }

  if (locale.includes(SEPARATOR)) {
    const [lang, region] = locale.split(SEPARATOR);
    return `${lang.toLowerCase()}-${region.toUpperCase()}`;
  }

  return `${locale.toLowerCase()}`;
};
